// The 8 transaction-phase catalog primitives (unchanged from before B-core).
export const RULE_LABELS = {
  allow_list: "Allow list",
  anchor_first: "Anchor first row",
  no_consecutive: "No consecutive",
  max_count: "Max count",
  ratio: "Max ratio",
  min_gap_after: "Min gap after",
  followed_by: "Followed by",
  section_order: "Section order",
  // B-core (LOCKED decision #4): the 6 enforced-but-previously-uncreatable
  // types — 5 completion-phase task requirements + category_consistency
  // (transaction phase). Brought the editable set from 8 to 14; time_realism
  // below makes 15. Always-on defaults (chronology_strict/balance_min/
  // spending_exhaustion/description_whitelist) are NOT rule rows and stay out
  // of this editor (read-only via Guardrails::Explainer elsewhere).
  //
  // NOTE the two kinds mixed in this list: most types ADD a constraint that does
  // not otherwise exist, but category_consistency and time_realism are already on
  // by default — a row for those only ever OVERRIDES the default (loosen/disable).
  required_spending_rows: "Required spending rows",
  required_final_balance: "Required final balance",
  required_final_row: "Required final row",
  min_time_span_hours: "Min time span",
  required_top_up_window_depletion: "Top-up window depletion",
  category_consistency: "Category consistency",
  // On by default (Rules::PolicyConfig), so unlike the rest of this list a row is
  // only ever created to OVERRIDE the default — loosen it, or switch it off for a
  // generation that wants round timestamps on purpose.
  time_realism: "Time realism"
};

export const RULE_DESCRIPTIONS = {
  allow_list: "Restrict spending to selected products or categories — any row outside the list is rejected.",
  anchor_first: "The very first spending row of the generation must belong to the chosen category.",
  no_consecutive: "Prevents two spending rows in a row from being the same product (mode: duplicate) or from the selected categories (mode: any).",
  max_count: "Caps how many rows a set of categories may have in total, either per-product or across the whole section.",
  ratio: "Caps a set of categories' share of ALL spending rows to a fraction (e.g. 0.3 = 30%).",
  min_gap_after: "After a row in the chosen category, the next row must land at least N minutes later.",
  followed_by: "A row in the \"from\" category must eventually be followed by a row in the \"to\" category (checked at completion time; the time window is read but not enforced by the engine).",
  section_order: "Enforces that categories appear in a specific order across the whole generation, checked at completion time — not while individual rows are being posted.",
  required_spending_rows: "The generation must end with exactly this many spending rows. Top-up rows are read-only and are never counted. Checked by verify_generation and complete_generation.",
  required_final_balance: "Sets a target final balance. Only ACTUALLY ENFORCED when \"Exact\" is checked — otherwise it's advisory only and nothing is validated against it.",
  required_final_row: "Constrains the LAST row of the generation: its description and/or amount and/or EUR amount. Any field left blank is not checked.",
  min_time_span_hours: "The first and last spending rows must be at least this many hours apart in timestamp.",
  required_top_up_window_depletion: "For every real gap between top-ups of at least the given number of minutes, the balance must be spent down to the given ceiling before the next top-up arrives.",
  category_consistency: "For any row in the configured categories (empty = all categories), the currency amount and the EUR amount must agree at the project's currency rate (within rounding). Disable to skip this check.",
  time_realism: "Rejects generated timestamps that look machine-made: a time on the round clock grid (:00 seconds AND minutes on the boundary below), or a gap that repeats identically several rows running. On by default — add this rule only to loosen or disable it. Imported rows and top-ups keep their original timestamps either way."
};

export const DEFAULT_PARAMS = {
  allow_list: { entries: [] },
  anchor_first: { category: null },
  no_consecutive: { categories: [], mode: "duplicate" },
  max_count: { categories: [], max: 1, scope: "product" },
  ratio: { categories: [], max: 0.3 },
  min_gap_after: { category: null, minutes: 30 },
  followed_by: { from: null, to: null },
  section_order: { categories: [], mode: "strict" },
  required_spending_rows: { value: 1 },
  required_final_balance: { value: 0, exact: false },
  required_final_row: { description: "", amount: null, amount_eur: null },
  min_time_span_hours: { value: 1 },
  required_top_up_window_depletion: { max_balance_before_next_top_up: 20, min_window_minutes: 30 },
  category_consistency: { enabled: true, categories: [] },
  // Mirrors Rules::PolicyConfig::DEFAULT_VALUES["time_realism"] so opening the form
  // shows what is already in force rather than a blank slate.
  time_realism: { enabled: true, minute_multiple: 5, max_uniform_gaps: 2 }
};

// Which `phase` a rule_type must be persisted with. The 7 transaction-phase
// engine primitives (Rules::Engine, evaluated per-row) + category_consistency
// (also a transaction-phase policy, evaluated per-row) are "transaction".
// section_order + the 5 required_* task requirements are only evaluated at
// completion time (GenerationFinalValidator / TaskRequirements), so they are
// "completion". Rules::PolicyConfig.for looks a rule up by rule_type alone
// (phase doesn't gate the lookup), but we still persist the correct phase so
// the data is honest and matches what Rules::Explainer / the rest of the app
// assumes.
export const RULE_PHASES = {
  allow_list: "transaction",
  anchor_first: "transaction",
  no_consecutive: "transaction",
  max_count: "transaction",
  ratio: "transaction",
  min_gap_after: "transaction",
  followed_by: "transaction",
  section_order: "completion",
  required_spending_rows: "completion",
  required_final_balance: "completion",
  required_final_row: "completion",
  min_time_span_hours: "completion",
  required_top_up_window_depletion: "completion",
  category_consistency: "transaction",
  time_realism: "transaction"
};

export function ruleLabel(rule) {
  return RULE_LABELS[rule.rule_type] || rule.rule_type;
}

export function ruleDescription(rule) {
  return RULE_DESCRIPTIONS[rule.rule_type] || "";
}

export function rulePhase(ruleType) {
  return RULE_PHASES[ruleType] || "transaction";
}
